import React, { ChangeEvent, useEffect } from 'react';

import { type FieldProps, Field, useFormikContext } from 'formik';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';
import { compose } from 'recompose';

import useRenderValidText from '../../../../custom-hooks/useRenderValidText';
import { formikValidate } from '../../../../helpers/validation';
import TextField from '../../../../ui/TextField/TextField';
import { icon } from '../../../WebsiteAndApp/helpers';
import StaffOnlyFormItemLayout from '../components/StaffOnlyFormItemLayout';
import { FormValues } from '../GeneralSettingsForm';

const validatePrivateSupportEmailField = (value: string) => {
  if (!value || !value.trim()) {
    return;
  }

  const maxLengthError = formikValidate.maxLength(255)(value);
  if (maxLengthError) {
    return maxLengthError;
  }

  return formikValidate.email(value);
};

type InnerProps = MappedProps;
type OuterProps = {
  isPrivateSupportEmailLoaded?: boolean;
  loadedPrivateSupportEmailAddress?: string;
};
type Props = InnerProps & OuterProps;

const PrivateSupportEmailAddressField = ({
  isPrivateSupportEmailLoaded = false,
  loadedPrivateSupportEmailAddress = '',
  translate,
}: Props) => {
  const { setFieldValue, touched } = useFormikContext<FormValues>();

  useEffect(() => {
    if (!isPrivateSupportEmailLoaded) {
      return;
    }

    if (touched.privateSupportEmailAddress) {
      return;
    }

    setFieldValue('privateSupportEmailAddress', loadedPrivateSupportEmailAddress);
  }, [
    isPrivateSupportEmailLoaded,
    loadedPrivateSupportEmailAddress,
    setFieldValue,
    touched.privateSupportEmailAddress,
  ]);

  return (
    <Permissions allowed={['FlipdishStaff']}>
      <StaffOnlyFormItemLayout labelId="Private_support_email_address">
        <Field name="privateSupportEmailAddress" validate={validatePrivateSupportEmailField}>
          {({ field, form }: FieldProps) => {
            const { errors, touched: formTouched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const showError = !!fieldError && (formTouched[field.name] as boolean | undefined);
            // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: pre-existing
            const helperText = useRenderValidText({
              fieldError,
              showError,
              touched: !!formTouched[field.name],
              value: field.value,
            });

            return (
              <TextField
                name={field.name}
                value={field.value}
                fdKey="Private-support-email-address"
                autoComplete="off"
                fullWidth
                type="email"
                style={{ paddingRight: 16 }}
                placeholder={translate('Email_address') as string}
                disabled={isSubmitting}
                error={showError}
                helperText={helperText}
                FormHelperTextProps={{
                  children: ' ',
                  style: { textAlign: 'right' },
                }}
                inputProps={{
                  style: {
                    paddingTop: 0,
                    height: '1.1876em',
                  },
                  maxLength: 255,
                }}
                InputProps={{
                  endAdornment: icon(showError, formTouched[field.name] as boolean, field.value),
                }}
                onBlur={field.onBlur}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  form.setFieldValue(field.name, e.target.value);
                  form.setFieldTouched(field.name, true, false);
                }}
              />
            );
          }}
        </Field>
      </StaffOnlyFormItemLayout>
    </Permissions>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state.locale),
});

export default compose<InnerProps, OuterProps>(connect(mapStateToProps))(
  PrivateSupportEmailAddressField
);
